import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAdminHeaders,
  hasPermission,
  PERMS,
  validateAdminSession,
} from '../../utils/adminAuth';
import './FeedbackPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const PAGE_SIZE = 30;

interface FeedbackEntry {
  id: number;
  createdAt: string;
  firstName: string;
  lastName: string;
  name: string;
  message: string;
  userId?: number;
}

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [entries, setEntries] = useState<FeedbackEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);
  const oldestIdRef = useRef<number | null>(null);

  useEffect(() => {
    const boot = async () => {
      const session = await validateAdminSession();
      if (!session) {
        navigate('/admin-login');
        return;
      }
      if (session.mustResetPassword) {
        navigate('/admin/change-password');
        return;
      }
      if (!hasPermission(PERMS.feedbackView, session) && !hasPermission(PERMS.feedbackRead, session)) {
        navigate('/admin-panel');
        return;
      }
      setIsValidating(false);
    };
    boot();
  }, [navigate]);

  const fetchPage = useCallback(async (replace: boolean) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (!replace && oldestIdRef.current) {
        params.set('before_id', String(oldestIdRef.current));
      }

      const response = await fetch(`${API_BASE_URL}/admin/feedback?${params}`, {
        headers: getAdminHeaders(),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to load feedback');
        return;
      }

      const page: FeedbackEntry[] = data.entries || [];
      setEntries((prev) => (replace ? page : [...prev, ...page]));
      setHasMore(Boolean(data.has_more));
      if (page.length > 0) {
        oldestIdRef.current = page[page.length - 1].id;
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isValidating) {
      oldestIdRef.current = null;
      fetchPage(true);
    }
  }, [isValidating, fetchPage]);

  useEffect(() => {
    if (isValidating || loading || !hasMore || error) return;
    const el = listRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 24) {
      fetchPage(false);
    }
  }, [entries, hasMore, loading, error, isValidating, fetchPage]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || !hasMore || loadingRef.current) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      fetchPage(false);
    }
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  if (isValidating) {
    return (
      <div className="feedback-page-container">
        <div className="feedback-page-card">
          <div className="loading-spinner" />
          <p>Validating session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-page-container">
      <div className="feedback-page-card">
        <div className="feedback-page-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="feedback-page-title">Feedback</h1>
          <p className="feedback-page-subtitle">
            Volunteer suggestions and comments, newest first
          </p>
        </div>

        <div
          className="feedback-list"
          ref={listRef}
          onScroll={handleScroll}
        >
          {error && <div className="feedback-status feedback-error">{error}</div>}
          {!error && entries.length === 0 && !loading && (
            <div className="feedback-status">No feedback yet.</div>
          )}
          {entries.map((entry) => (
            <article key={entry.id} className="feedback-entry">
              <div className="feedback-entry-meta">
                <span className="feedback-name">{entry.name}</span>
                <span className="feedback-when">{formatWhen(entry.createdAt)}</span>
              </div>
              <p className="feedback-message">{entry.message}</p>
            </article>
          ))}
          {loading && <div className="feedback-status">Loading…</div>}
          {!loading && hasMore && (
            <button type="button" className="load-more-btn" onClick={() => fetchPage(false)}>
              Load older
            </button>
          )}
          {!loading && !hasMore && entries.length > 0 && (
            <div className="feedback-status feedback-end">End of feedback</div>
          )}
        </div>

        <div className="feedback-page-footer">
          <button type="button" onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
